import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { resourcesApi } from '@/utils/api';
import type { Resource, ResourceType } from '@/types';
import type { RootState } from '../index';

interface ResourcesState {
  entities: Resource[];
  currentEntity: Resource | null;
  filters: {
    type: ResourceType | null;
    level: string | null; // resourceLevelId
    active: boolean | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: ResourcesState = {
  entities: [],
  currentEntity: null,
  filters: {
    type: null,
    level: null,
    active: null,
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchResources = createAsyncThunk(
  'resources/fetchAll',
  async (params: { type?: ResourceType; level?: string; active?: boolean } | undefined, { rejectWithValue }) => {
    try {
      const response = await resourcesApi.getAll(params);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch resources');
      }
      return (response.data as any)?.resources || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch resources');
    }
  }
);

export const fetchResourceById = createAsyncThunk(
  'resources/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourcesApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch resource');
      }
      return (response.data as any)?.resource || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch resource');
    }
  }
);

export const createResource = createAsyncThunk(
  'resources/create',
  async (data: Partial<Resource>, { rejectWithValue }) => {
    try {
      const response = await resourcesApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create resource');
      }
      return (response.data as any)?.resource || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create resource');
    }
  }
);

export const updateResource = createAsyncThunk(
  'resources/update',
  async ({ id, data }: { id: string; data: Partial<Resource> }, { rejectWithValue }) => {
    try {
      const response = await resourcesApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update resource');
      }
      return (response.data as any)?.resource || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update resource');
    }
  }
);

export const deleteResource = createAsyncThunk(
  'resources/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourcesApi.delete(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete resource');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete resource');
    }
  }
);

const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<Resource | null>) => {
      state.currentEntity = action.payload;
    },
    setTypeFilter: (state, action: PayloadAction<ResourceType | null>) => {
      state.filters.type = action.payload;
    },
    setLevelFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.level = action.payload;
    },
    setActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.filters.active = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        type: null,
        level: null,
        active: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchResources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchResourceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchResourceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createResource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createResource.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.unshift(action.payload);
        }
      })
      .addCase(createResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateResource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateResource.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(r => r.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteResource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(r => r.id !== action.payload);
        if (state.currentEntity?.id === action.payload) {
          state.currentEntity = null;
        }
      })
      .addCase(deleteResource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentEntity,
  setTypeFilter,
  setLevelFilter,
  setActiveFilter,
  clearFilters,
} = resourcesSlice.actions;

// Selectors
export const selectAllResources = (state: RootState) => state.resources.entities;
export const selectActiveResources = (state: RootState) =>
  state.resources.entities.filter(r => r.isActive);
export const selectResourcesByType = (state: RootState, type: ResourceType) =>
  state.resources.entities.filter(r => r.resourceType === type);
export const selectResourceFilters = (state: RootState) => state.resources.filters;
export const selectResourcesLoading = (state: RootState) => state.resources.loading;
export const selectResourcesError = (state: RootState) => state.resources.error;
export const selectCurrentResource = (state: RootState) => state.resources.currentEntity;

export default resourcesSlice.reducer;
